import { ReactElement, useMemo } from 'react'
import { ContactAdditionalFieldsV2 } from './ContactAdditionalFieldsV2'
import { ContactPipelinesV2 } from './ContactPipelinesV2'
import ContactSectionsV2 from './ContactSectionsV2'
import { ContactSpotlight } from './ContactSpotlight'
import { useSettings } from 'app/hooks/useSettings'
import type { ContactJson } from 'app/types/entities'
import { getContactDrawerSections, SectionItem } from 'lib/contactDrawerConfig'

type Props = {
  contact: ContactJson
  isIntegrated: boolean
  isAgentOnly?: boolean
  isAIThread?: boolean
  onContactChange?: (contact: ContactJson) => void
}

/**
 * ContactDrawerContainer - Handles the customizable container section (Section 3) of ContactInfo
 *
 * This component is responsible for rendering 3 customizable subsections:
 * - Spotlight: Key fields with customize button
 * - Pipelines: Pipeline counter badge + add to pipeline
 * - Additional Fields: Collapsible dropdown with field subsections
 *
 * The order and visibility of these sections is controlled by the user's customization
 * settings stored in 'contact-drawer-config'.
 */
export function ContactDrawerContainer({
  contact,
  isIntegrated,
  isAgentOnly = false,
  isAIThread,
  onContactChange,
}: Props): ReactElement {
  const [customSections] = useSettings<SectionItem[]>(`contact-drawer-config`)

  // Memoize sections calculation to prevent unnecessary recalculation
  const sections = useMemo(
    () => getContactDrawerSections(customSections, contact.orgId),
    [customSections, contact.orgId],
  )

  // Memoize filtered sections to prevent creating new array references
  const customizableSections = useMemo(
    () =>
      sections.filter((section: SectionItem) =>
        [`spotlight`, `pipelines`, `additional-fields`, `stats`].includes(section.id),
      ),
    [sections],
  )

  return (
    <>
      {customizableSections.map((section) => {
        if (!section.visible) return null

        switch (section.id) {
          case `spotlight`:
            return (
              <ContactSpotlight
                key={section.id}
                contact={contact}
                config={section}
                isAgentOnly={isAgentOnly}
                isAIThread={isAIThread}
              />
            )
          case `pipelines`:
            // Hide pipelines section for Agent Only workspaces
            if (isAgentOnly) return null
            return <ContactPipelinesV2 key={section.id} contact={contact} onContactChange={onContactChange} />
          case `additional-fields`:
            return (
              <ContactAdditionalFieldsV2
                key={section.id}
                contact={contact}
                config={section}
                isAgentOnly={isAgentOnly}
              />
            )
          case `stats`:
            return <ContactSectionsV2 key={section.id} contact={contact} isIntegrated={isIntegrated} />
          default:
            return null
        }
      })}
    </>
  )
}
